/**
 * SearchResults - Renders search result items with type badges and semantic indicators
 */

import { Fragment } from 'react'
import { ArrowRight, Sparkles, Search, AlertTriangle } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { SearchResult } from './types'
import { getIcon, getColorClass, typeLabels, SEARCH_SECTIONS } from './types'
import { RecentSearchesSection } from './RecentSearchesSection'
import { highlightMatch } from './highlight'
import { emitSearchTelemetry } from './telemetry'
import { recordFrecencySelection } from './frecency'

interface SearchResultsProps {
  results: SearchResult[]
  selectedIndex: number
  semanticAvailable: boolean
  searchError: string | null
  query: string
  isLoading: boolean
  recentSearches: string[]
  onSelectRecentSearch: (query: string) => void
  onRecordRecentSearch: (query: string) => void
  onClose: () => void
}

export function SearchResults({
  results,
  selectedIndex,
  semanticAvailable,
  searchError,
  query,
  isLoading,
  recentSearches,
  onSelectRecentSearch,
  onRecordRecentSearch,
  onClose,
}: SearchResultsProps) {
  return (
    <>
      {/* Search Results */}
      {query.trim() && results.length > 0 && (() => {
        // Board-first sectioned palette (2026-06-03). When ANY result
        // carries a `section`, render a header per section change
        // (the list is already section-ordered by `useSearch`).
        // Otherwise render the legacy single "Results" header.
        const hasSections = results.some((r) => !!r.section)
        // G4 — the kanban provider tags rows with `metadata.kanban.
        // truncated` when the BE capped the result set. Surface a single
        // "refine to narrow" footer so the operator knows the shown rows
        // aren't the full match set (the BE deliberately doesn't COUNT(*),
        // so we can't show an exact total — the hint is qualitative).
        const kanbanTruncated = results.some(
          (r) => (r.metadata?.kanban as { truncated?: boolean } | undefined)?.truncated,
        )
        let lastSection: string | undefined = undefined
        let firstHeaderRendered = false
        return (
          <div className="p-2">
            {!hasSections && (
              <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                <span>Results</span>
                {semanticAvailable && (
                  <span className="flex items-center gap-1 text-[10px] text-purple-500 bg-purple-50 dark:bg-purple-900/20 px-1.5 py-0.5 rounded">
                    <Sparkles className="w-3 h-3" />
                    AI Enhanced
                  </span>
                )}
              </div>
            )}

            {results.map((result, i) => {
              const Icon = getIcon(result.icon)
              const showHeader = hasSections && result.section !== lastSection
              if (showHeader) lastSection = result.section
              const headerLabel = result.section
                ? SEARCH_SECTIONS[result.section]?.label ?? result.section
                : 'Results'
              const headerWithBadge = showHeader && !firstHeaderRendered
              if (showHeader) firstHeaderRendered = true
              return (
                <Fragment key={`${result.providerId || result.type}-${result.id}`}>
                  {showHeader && (
                    <div className="px-3 py-1.5 mt-1 text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                      <span>{headerLabel}</span>
                      {headerWithBadge && semanticAvailable && (
                        <span className="flex items-center gap-1 text-[10px] text-purple-500 bg-purple-50 dark:bg-purple-900/20 px-1.5 py-0.5 rounded">
                          <Sparkles className="w-3 h-3" />
                          AI Enhanced
                        </span>
                      )}
                    </div>
                  )}
                  <button
                    data-index={i}
                    onClick={() => {
                      if (query.trim()) onRecordRecentSearch(query)
                      // Mouse-path selection telemetry (keyboard path emits
                      // in useSearch's Enter handler).
                      emitSearchTelemetry({
                        kind: 'result_selected',
                        query,
                        index: i,
                        resultId: String(result.id),
                        resultType: result.type,
                        providerId: result.providerId,
                      })
                      recordFrecencySelection(result)
                      if (result.url) window.location.href = result.url
                      onClose()
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors group',
                      selectedIndex === i
                        ? 'bg-primary/10'
                        : 'hover:bg-muted/60'
                    )}
                  >
                    <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', getColorClass(result.color))}>
                      <Icon className="w-4 h-4" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate">
                          {highlightMatch(result.title, query, `t-${i}`)}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                          {typeLabels[result.type] || result.type}
                        </span>
                        {result.search_type === 'semantic' && (
                          <Sparkles className="w-3 h-3 text-purple-400" />
                        )}
                      </div>
                      {result.subtitle && (
                        <div className="text-xs text-muted-foreground truncate">
                          {highlightMatch(result.subtitle, query, `s-${i}`)}
                        </div>
                      )}
                      {result.description && (
                        <div className="text-xs text-muted-foreground/80 truncate mt-0.5">
                          {highlightMatch(result.description, query, `d-${i}`)}
                        </div>
                      )}
                    </div>

                    <ArrowRight className="w-4 h-4 text-muted-foreground/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                </Fragment>
              )
            })}
            {kanbanTruncated && (
              <div className="px-3 py-2 mt-1 text-xs text-muted-foreground border-t border-border/60">
                Showing the top matches — refine your search to narrow the
                results.
              </div>
            )}
          </div>
        )
      })()}

      {/* Error Banner — BUG-2026-06-10-SEARCH-ERROR-BANNER-INVISIBLE:
          previously gated on `results.length > 0`, so a failed search
          that returned ZERO rows (the common case — the network/parse
          error IS why there are no rows) rendered no visible banner; the
          operator just saw an empty palette and assumed "no matches".
          Render whenever an error is present so the failure is always
          surfaced. The no-results block below no longer repeats it. */}
      {searchError && (
        <div
          role="alert"
          className="mx-3 mt-1 px-3 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5"
        >
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          {searchError}
        </div>
      )}

      {/* No Results */}
      {query.trim() && !isLoading && results.length === 0 && (
        <div className="px-4 py-8">
          <div className="text-center mb-6">
            <Search className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
            <div className="text-muted-foreground">No results found for "{query}"</div>
            <div className="text-sm text-muted-foreground/80 mt-1">
              Try a different search term or adjust filters
            </div>
          </div>
          <RecentSearchesSection
            recentSearches={recentSearches}
            onSelect={onSelectRecentSearch}
            filterQuery={query}
            maxItems={5}
            className="px-0"
          />
        </div>
      )}
    </>
  )
}
